import { isAxiosError } from 'axios';
import type { AnyFormApi } from '@tanstack/react-form';

interface ValidationProblemDetailsLike {
  errors?: Record<string, string[]>;
}

/** Mapování názvu pole z BE (case-insensitive) na cestu pole ve formuláři (FE). */
export type ServerFieldMap = Record<string, string>;

/**
 * Promítne serverové validační chyby (RFC 9457 `ValidationProblemDetails` z 400 odpovědi)
 * přímo na příslušná pole TanStack Form přes `setFieldMeta`.
 *
 * Vrací `true`, pokud byla namapována alespoň jedna chyba (volající pak nemusí zobrazovat
 * generický toast). Klíče v `fieldMap` se porovnávají case-insensitive (BE posílá typicky
 * PascalCase názvy property z FluentValidation).
 */
export function mapServerValidationErrors(
  form: AnyFormApi,
  error: unknown,
  fieldMap: ServerFieldMap
): boolean {
  if (!isAxiosError(error)) return false;

  const data = error.response?.data as ValidationProblemDetailsLike | undefined;
  const errors = data?.errors;
  if (!errors) return false;

  const normalizedMap: Record<string, string> = {};
  for (const [serverKey, target] of Object.entries(fieldMap)) {
    normalizedMap[serverKey.toLowerCase()] = target;
  }

  let mapped = false;
  for (const [serverField, messages] of Object.entries(errors)) {
    const target = normalizedMap[serverField.toLowerCase()];
    if (!target || messages.length === 0) continue;

    form.setFieldMeta(target, (meta) => ({
      ...meta,
      isTouched: true,
      errors: messages.map((message) => ({ message })),
    }));
    mapped = true;
  }

  return mapped;
}
